import { type GetListParams } from "react-admin";

import { createApiClient } from "@/api";
import { type DataProviderSchema } from "@/utils";
import { getLocalFilteredSortedList } from "@/utils/listParams";

const apiClient = createApiClient("jsonld");
const BASE_URL = "/api/vpn/country_costs";
const DEFAULT_PER_PAGE = 10;

const fetchCountryPrices = async ({ pagination }: GetListParams) => {
  const { data } = await apiClient.GET(BASE_URL, {
    params: {
      query: {
        page: pagination?.page ?? 1,
        limit: pagination?.perPage ?? DEFAULT_PER_PAGE,
        pagination: true,
      },
    },
  });

  return {
    data: (data?.["hydra:member"] ?? []).map((item) => ({
      ...item,
      id: item.country,
    })),
    total: data?.["hydra:totalItems"] ?? 0,
  };
};

export const countryPricesSchema: DataProviderSchema<"CountryCost", "jsonld"> =
  {
    list: getLocalFilteredSortedList(fetchCountryPrices),
    detail: async ({ id }) => {
      const { data } = await apiClient.GET(`${BASE_URL}/{id}`, {
        params: {
          path: { id: String(id) },
        },
      });

      if (!data) throw new Error("Country not found");

      return { data: { ...data, id: data.country } };
    },
    edit: async ({ id, data: { country, trialCost } }) => {
      if (!country) throw Error("Country is required");
      const { data } = await apiClient.PUT(`${BASE_URL}/{id}`, {
        params: {
          path: { id: String(id) },
        },
        body: {
          country,
          trialCost: String(trialCost),
        },
      });

      if (!data) throw Error("Something went wrong");

      return { data: { ...data, id: data.country } };
    },
    create: async ({ data: { country, trialCost } }) => {
      if (!country) throw Error("Country is required");
      const { data } = await apiClient.POST(BASE_URL, {
        body: {
          country,
          trialCost: String(trialCost),
        },
      });

      if (!data) throw Error("Something went wrong");

      return { data: { ...data, id: data.country } };
    },
  };
